import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Paper,
} from '@mui/material';
import { FaSearch, FaPlus, FaCheck } from 'react-icons/fa';
import Modal from '../../../common/Modal';
import LoadingSpinner from '../../../common/LoadingSpinner';
import MeetingTypeFilterSelector from '../../../common/filters/MeetingTypeFilterSelector';
import { meetingsApi } from '../../../../services/api/meetingsApi';

const SystemTalkingPointsModal = ({
  isOpen,
  onClose,
  meetingTypeIds,
  addedTalkingPointIds,
  onAddTalkingPoint,
}) => {
  const [systemTalkingPoints, setSystemTalkingPoints] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMeetingTypeFilter, setSelectedMeetingTypeFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, meetingTypeIds]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const points = await meetingsApi.getSystemTalkingPoints(meetingTypeIds);
      setSystemTalkingPoints(points || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    setSelectedMeetingTypeFilter('');
    onClose();
  };

  const filteredPoints = systemTalkingPoints.filter(point => {
    // Search filter
    const search = searchTerm.toLowerCase();
    const matchesSearch = !search ||
      point.question?.toLowerCase().includes(search) ||
      point.instructions?.toLowerCase().includes(search);

    // Meeting type filter
    const matchesMeetingType = !selectedMeetingTypeFilter ||
      point.meetingTypes?.some(mt => mt.meetingType?.id === selectedMeetingTypeFilter || mt.id === selectedMeetingTypeFilter);

    return matchesSearch && matchesMeetingType;
  });

  const isAlreadyAdded = (pointId) => {
    return addedTalkingPointIds.includes(pointId);
  };

  // Get meeting type IDs that are actually used by the talking points
  const usedMeetingTypeIds = [...new Set(
    systemTalkingPoints.flatMap(tp =>
      tp.meetingTypes?.map(mt => mt.meetingType?.id || mt.id) || []
    )
  )];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={(e) => {
        e.preventDefault();
        handleClose();
      }}
      title="Add System Talking Points"
      submitText="Done"
      cancelText="Cancel"
      width="700px"
      maxWidth="90vw"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Search and Filter Row */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            sx={{ flex: 1 }}
            placeholder="Search talking points..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            autoComplete="off"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch style={{ color: '#6B7280' }} />
                </InputAdornment>
              ),
            }}
          />
          <MeetingTypeFilterSelector
            value={selectedMeetingTypeFilter}
            onChange={setSelectedMeetingTypeFilter}
            limitToTypeIds={meetingTypeIds}
          />
        </Box>

        <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
          {isLoading ? (
            <LoadingSpinner />
          ) : filteredPoints.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {searchTerm || selectedMeetingTypeFilter ? 'No matching talking points found' : 'No system talking points available'}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {filteredPoints.map((point) => {
                const alreadyAdded = isAlreadyAdded(point.id);
                return (
                  <Paper
                    key={point.id}
                    sx={{
                      p: 2,
                      cursor: alreadyAdded ? 'default' : 'pointer',
                      backgroundColor: alreadyAdded ? '#F0FDF4' : '#fff',
                      border: '1px solid',
                      borderColor: alreadyAdded ? '#86EFAC' : '#E2E8F0',
                      opacity: alreadyAdded ? 0.8 : 1,
                      transition: 'all 0.2s ease',
                      '&:hover': alreadyAdded ? {} : {
                        borderColor: '#2563EB',
                        backgroundColor: '#EFF6FF',
                      },
                    }}
                    onClick={() => {
                      if (!alreadyAdded) {
                        onAddTalkingPoint(point);
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight={500} sx={{ mb: 0.5 }}>
                          {point.question}
                        </Typography>
                        {point.instructions && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {point.instructions}
                          </Typography>
                        )}
                        {/* Meeting Types */}
                        {point.meetingTypes && point.meetingTypes.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {point.meetingTypes.map((mt) => {
                              const meetingType = mt.meetingType || mt;
                              return (
                                <Chip
                                  key={meetingType.id}
                                  label={meetingType.name}
                                  size="small"
                                  sx={{
                                    fontSize: '10px',
                                    height: 20,
                                    backgroundColor: meetingType.color || '#E5E7EB',
                                    color: meetingType.color ? '#fff' : '#374151',
                                  }}
                                />
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ flexShrink: 0 }}>
                        {alreadyAdded ? (
                          <Chip
                            icon={<FaCheck size={10} />}
                            label="Added"
                            size="small"
                            sx={{
                              backgroundColor: '#22C55E',
                              color: '#fff',
                              fontSize: '11px',
                              height: 24,
                              '& .MuiChip-icon': { color: '#fff' },
                            }}
                          />
                        ) : (
                          <Chip
                            icon={<FaPlus size={10} />}
                            label="Add"
                            size="small"
                            sx={{
                              backgroundColor: '#2563EB',
                              color: '#fff',
                              fontSize: '11px',
                              height: 24,
                              '& .MuiChip-icon': { color: '#fff' },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          {filteredPoints.filter(p => !addedTalkingPointIds.includes(p.id)).length} talking point{filteredPoints.filter(p => !addedTalkingPointIds.includes(p.id)).length !== 1 ? 's' : ''} available to add
          {addedTalkingPointIds.length > 0 && ` • ${addedTalkingPointIds.length} already added`}
        </Typography>
      </Box>
    </Modal>
  );
};

export default SystemTalkingPointsModal;
